import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';

import { useAllow } from '@components/features/Restricted';
import { getAsvOnboardingHost } from '@routes/asvOnboardingStorage';
import { api } from '@services/apiService';
import { ENV } from '@utils/env';
import { users as usersResource } from '@utils/resources/users';

import { useTeamQuery } from './team';

// Scope Types

export const USER_QUERY_KEY = ['user'];

// Query Definitions

const userQuery = {
  staleTime: 60 * 1000,
  queryKey: USER_QUERY_KEY,
  queryFn: async () => {
    // ASV Scans need a production app, so a user signing up through the ASV
    // flow gets one instead of a sandbox app. The API only uses this when it
    // creates the user's first team.
    // TODO: keep this query generic and send prod=true from /asv/checkout
    // alone. That needs Monitoring and anything else that loads the user to
    // wait for it, or one of them can create the team without it.
    const searchParams = getAsvOnboardingHost() ? { prod: true } : undefined;
    const response = await api.get(usersResource(), { searchParams });

    /** @type {import('types/users').User} */
    const user = await response.json();

    return {
      _auth: true,
      ...user,
    };
  },
};

// Query Selectors
/**
 * @param {import('types/users').User} data
 * @return {import('types/users').User | null}
 */
const userSelector = (data) => (data._auth === true ? data.user : null);

/**
 * @param {import('types/users').User} data
 * @return {import('types/users').User['teams'] | null}
 */
export const userTeamsSelector = (data) => userSelector(data)?.teams ?? null;

// Query Hooks
/**
 * @template [T=import('types/users').User]
 * @param {import('@tanstack/react-query').UseQueryOptions<import('types/users').User, unknown, T>} [params]
 * @return {import('@tanstack/react-query').UseQueryResult<T, unknown>}
 */
export const useUserQuery = (params) =>
  useQuery({
    ...userQuery,
    ...params,
    select: params?.select ?? userSelector,
    enabled: params?.enabled ?? true,
  });

export const useRequestPasswordChange = (opts = {}) => {
  const { data: user } = useUserQuery();

  return useMutation({
    mutationFn: async () => {
      return fetch(
        `https://${ENV.auth0.domain}/dbconnections/change_password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            client_id: ENV.auth0.clientId,
            email: user.email,
            connection: 'Username-Password-Authentication',
          }),
        }
      );
    },
    ...opts,
  });
};

export function useCurrentTeam() {
  const { teamUuid } = useParams();
  const { data, isInitialLoading } = useUserQuery();

  const fromUserData = (data?.teams || []).find((t) => t.uuid === teamUuid);
  const { enabled: isInternalAdmin } = useAllow('internal');
  const shouldUseTeamQuery = !fromUserData && isInternalAdmin && !!teamUuid;

  const teamQuery = useTeamQuery(teamUuid, {
    enabled: shouldUseTeamQuery,
  });

  if (shouldUseTeamQuery) {
    return {
      loading: teamQuery.isInitialLoading,
      team: teamQuery.data,
    };
  }

  return {
    loading: isInitialLoading,
    team: fromUserData,
  };
}

export function useCurrentApp() {
  const { appUuid } = useParams();
  const { loading, team } = useCurrentTeam();

  return {
    loading,
    app: (team?.apps || []).find((app) => app.uuid === appUuid),
  };
}

export function teamHasBillingError(team) {
  return (
    team?.tier === 'pro' && !team?.stripeCustomerId && !team?.algunaCustomerId
  );
}
